function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import withStyles from 'isomorphic-style-loader/withStyles';
import { injectIntl } from 'react-intl';
import intlShape from 'shapes/intlShape';
import { isManualReading } from 'helpers/externalDataSources';
import { isAggregatedPostMeal } from 'libs/StatsCalculations';
import messages from '../../../messages';
import styles from './Table.pcss';
var _React_Component;
class Table extends (_React_Component = React.Component) {
    calculateManualReadings(readings) {
        return readings.filter((item)=>isManualReading(item)).length;
    }
    groupReadings(readings) {
        const preMealReadings = [];
        const postMealReadings = [];
        readings.forEach((reading)=>{
            if (isAggregatedPostMeal(reading.flags)) {
                postMealReadings.push(reading);
            } else {
                preMealReadings.push(reading);
            }
        });
        return {
            preMealReadings,
            postMealReadings
        };
    }
    calculateAverageValue(readings) {
        const { conversion } = this.props;
        const averageValue = readings.reduce((a, b)=>a + (conversion.toDisplay(b.value) || 0), 0) / readings.length;
        return averageValue ? averageValue.toFixed(2) : 0;
    }
    standardDeviation(readings) {
        const { conversion } = this.props;
        const averageReadings = this.calculateAverageValue(readings);
        return Math.sqrt(readings.reduce((a, b)=>a + ((conversion.toDisplay(b.value) - averageReadings) ** 2 || 0), 0) / readings.length).toFixed(2);
    }
    renderRow(days, readings) {
        const { conversion } = this.props;
        if (isEmpty(readings)) return null;
        const manualReadings = this.calculateManualReadings(readings);
        const automaticReadings = readings.length - manualReadings;
        const readingsPerDay = (readings.length / 30).toFixed(2);
        const { preMealReadings, postMealReadings } = this.groupReadings(readings);
        const averagePreMeal = this.calculateAverageValue(preMealReadings);
        const averagePostMeal = this.calculateAverageValue(postMealReadings);
        const standardDeviation = this.standardDeviation(readings);
        const minValue = conversion.toDisplay(Math.min(...readings.map((reading)=>reading.value)));
        const maxValue = conversion.toDisplay(Math.max(...readings.map((reading)=>reading.value)));
        const CV = (standardDeviation / this.calculateAverageValue(readings) * 100).toFixed(2);
        return /*#__PURE__*/ _jsxs("tr", {
            children: [
                /*#__PURE__*/ _jsx("td", {
                    children: days
                }),
                /*#__PURE__*/ _jsx("td", {
                    children: automaticReadings || '-'
                }),
                /*#__PURE__*/ _jsx("td", {
                    children: manualReadings || '-'
                }),
                /*#__PURE__*/ _jsx("td", {
                    children: readingsPerDay || '-'
                }),
                /*#__PURE__*/ _jsx("td", {
                    children: averagePreMeal || '-'
                }),
                /*#__PURE__*/ _jsx("td", {
                    children: averagePostMeal || '-'
                }),
                /*#__PURE__*/ _jsx("td", {
                    className: styles.bold,
                    children: standardDeviation || '-'
                }),
                /*#__PURE__*/ _jsx("td", {
                    children: `${minValue} - ${maxValue}`
                }),
                /*#__PURE__*/ _jsx("td", {
                    className: styles.bold,
                    children: CV ? `${CV}%` : '-'
                })
            ]
        });
    }
    render() {
        const { readings30, readings60, readings90, conversion } = this.props;
        return /*#__PURE__*/ _jsx("div", {
            className: styles.bloodGlucoseConcenctration__wrapper,
            children: /*#__PURE__*/ _jsx("table", {
                children: /*#__PURE__*/ _jsxs("tbody", {
                    children: [
                        /*#__PURE__*/ _jsxs("tr", {
                            children: [
                                /*#__PURE__*/ _jsx("th", {
                                    children: this.props.intl.formatMessage(messages.glucoseReport.tableDays)
                                }),
                                /*#__PURE__*/ _jsx("th", {
                                    children: this.props.intl.formatMessage(messages.glucoseReport.tableAutomaticReadings)
                                }),
                                /*#__PURE__*/ _jsx("th", {
                                    children: this.props.intl.formatMessage(messages.glucoseReport.tableManualReadings)
                                }),
                                /*#__PURE__*/ _jsx("th", {
                                    children: this.props.intl.formatMessage(messages.glucoseReport.tableReadingsPerDay)
                                }),
                                /*#__PURE__*/ _jsxs("th", {
                                    children: [
                                        this.props.intl.formatMessage(messages.glucoseReport.tableAveragePreMeal),
                                        ` (${conversion.unitSymbol})`
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("th", {
                                    children: [
                                        this.props.intl.formatMessage(messages.glucoseReport.tableAveragePostMeal),
                                        ` (${conversion.unitSymbol})`
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("th", {
                                    className: styles.bold,
                                    children: [
                                        this.props.intl.formatMessage(messages.glucoseReport.tableStandardDeviation),
                                        ` (${conversion.unitSymbol})`
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("th", {
                                    children: [
                                        this.props.intl.formatMessage(messages.glucoseReport.tableRange),
                                        " ",
                                        `(${conversion.unitSymbol})`
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("th", {
                                    className: styles.bold,
                                    children: [
                                        this.props.intl.formatMessage(messages.glucoseReport.tableCV),
                                        " (%)"
                                    ]
                                })
                            ]
                        }),
                        this.renderRow(30, readings30),
                        this.renderRow(60, readings60),
                        this.renderRow(90, readings90)
                    ]
                })
            })
        });
    }
}
_define_property(Table, "propTypes", {
    // Implicit props
    intl: intlShape.isRequired,
    // Explicit props
    readings30: PropTypes.array,
    readings60: PropTypes.array,
    readings90: PropTypes.array,
    conversion: PropTypes.object
});
export default withStyles(styles)(injectIntl(Table));
